import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Table } from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import type { AssetCategory, LocaleCode } from '@/api/models';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import {
    isoToDisplayDate,
    NIL_AMOUNT,
    resolveLabel,
} from '@/pages/accounting/statements/statementMeta';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ENTRY_KIND_KEY,
    formatPeriod,
    methodLabelKey,
} from '../../fixedAssetsMeta';
import { useAssetDetailDrawer } from './useAssetDetailDrawer';
import { AssetStatusChip } from '../assetStatusChip';

type AssetDetailDrawerProps = {
    /** The asset to show; `null` closes the drawer. */
    assetId: string | null;
    categories: AssetCategory[];
    locale: LocaleCode;
    onClose: () => void;
    onEdit: (assetId: string) => void;
    onDispose: (assetId: string) => void;
};

const Field: React.FC<{ label: string; value: string }> = ({
    label,
    value,
}) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        <span className="text-sm tabular-nums text-foreground">{value}</span>
    </div>
);

/**
 * Right-side asset detail drawer (HeroUI Pro `Sheet`) — sibling of the journal
 * entry drawer. Self-served: it fetches the asset with its schedule and the
 * entries that touched it.
 *
 * The **depreciation schedule** is the payoff view: period by period, the charge,
 * the running accumulated total and the resulting net book value, with the rows a
 * committed run has already produced marked as posted. It is a projection of the
 * same arithmetic the runs apply, so the last row always lands on cost − salvage
 * exactly.
 *
 * The entry list is the audit trail — the acquisition entry and every
 * depreciation and disposal entry — each linking into the journal.
 */
export const AssetDetailDrawer: React.FC<AssetDetailDrawerProps> = ({
    assetId,
    categories,
    locale,
    onClose,
    onEdit,
    onDispose,
}) => {
    const { t } = useTranslation('pages');
    const {
        detail,
        asset,
        categoryLabel,
        scheduleRows,
        isLoading,
        onOpenEntry,
    } = useAssetDetailDrawer({ assetId, categories, locale });

    return (
        <Sheet
            isOpen={assetId !== null}
            onOpenChange={(open) => !open && onClose()}
            placement="right"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[640px]">
                    <Sheet.Dialog
                        aria-label={
                            asset
                                ? resolveLabel(asset.name, locale, asset.id)
                                : t('fixedAssets.detail.title')
                        }
                        {...getNodeTestIdProp('fixed-assets-detail-drawer')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header>
                            <Sheet.Heading>
                                <span className="flex flex-wrap items-center gap-3">
                                    <span
                                        className="text-lg font-semibold"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-detail-name',
                                        )}
                                    >
                                        {asset
                                            ? resolveLabel(
                                                  asset.name,
                                                  locale,
                                                  asset.id,
                                              )
                                            : ''}
                                    </span>
                                    {asset ? (
                                        <AssetStatusChip
                                            slot={`detail-${asset.id}`}
                                            status={asset.status}
                                        />
                                    ) : null}
                                </span>
                            </Sheet.Heading>
                        </Sheet.Header>

                        <Sheet.Body className="flex flex-col gap-6">
                            {isLoading ? (
                                <div className="flex flex-col items-center gap-2 py-10 text-muted">
                                    <Icon
                                        aria-hidden
                                        className="size-7 animate-spin"
                                        icon="gravity-ui:arrows-rotate-right"
                                    />
                                    <span className="text-sm">
                                        {t('fixedAssets.detail.loading')}
                                    </span>
                                </div>
                            ) : asset && detail ? (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Field
                                            label={t(
                                                'fixedAssets.columns.category',
                                            )}
                                            value={categoryLabel}
                                        />
                                        <Field
                                            label={t(
                                                'fixedAssets.columns.acquisitionDate',
                                            )}
                                            value={isoToDisplayDate(
                                                asset.acquisitionDate,
                                            )}
                                        />
                                        <Field
                                            label={t(
                                                'fixedAssets.columns.cost',
                                            )}
                                            value={formatGel(asset.costTetri)}
                                        />
                                        <Field
                                            label={t(
                                                'fixedAssets.detail.salvage',
                                            )}
                                            value={formatGel(
                                                asset.salvageValueTetri,
                                            )}
                                        />
                                        <Field
                                            label={t(
                                                'fixedAssets.columns.accumulated',
                                            )}
                                            value={
                                                asset.accumulatedDepreciationTetri ===
                                                0
                                                    ? NIL_AMOUNT
                                                    : formatGel(
                                                          asset.accumulatedDepreciationTetri,
                                                      )
                                            }
                                        />
                                        <Field
                                            label={t(
                                                'fixedAssets.columns.netBookValue',
                                            )}
                                            value={formatGel(
                                                asset.netBookValueTetri,
                                            )}
                                        />
                                        <Field
                                            label={t(
                                                'fixedAssets.columns.method',
                                            )}
                                            value={t(
                                                methodLabelKey(asset.method),
                                            )}
                                        />
                                        <Field
                                            label={t(
                                                'fixedAssets.columns.usefulLife',
                                            )}
                                            value={t('fixedAssets.lifeMonths', {
                                                count: asset.usefulLifeMonths,
                                            })}
                                        />
                                        {asset.disposalDate ? (
                                            <Field
                                                label={t(
                                                    'fixedAssets.detail.disposedOn',
                                                )}
                                                value={isoToDisplayDate(
                                                    asset.disposalDate,
                                                )}
                                            />
                                        ) : null}
                                        {asset.disposalProceedsTetri !==
                                        null ? (
                                            <Field
                                                label={t(
                                                    'fixedAssets.dispose.fields.proceeds',
                                                )}
                                                value={formatGel(
                                                    asset.disposalProceedsTetri,
                                                )}
                                            />
                                        ) : null}
                                    </div>

                                    {/* Depreciation schedule — the payoff view */}
                                    <div className="flex flex-col gap-2">
                                        <span className="text-xs font-medium uppercase tracking-wide text-muted">
                                            {t('fixedAssets.detail.schedule')}
                                        </span>
                                        {scheduleRows.length === 0 ? (
                                            <p className="text-sm text-muted">
                                                {t(
                                                    'fixedAssets.detail.noSchedule',
                                                )}
                                            </p>
                                        ) : (
                                            <div
                                                {...getNodeTestIdProp(
                                                    'fixed-assets-schedule-table',
                                                )}
                                            >
                                                <Table>
                                                    <Table.ScrollContainer className="max-h-80 overflow-y-auto">
                                                        <Table.Content
                                                            aria-label={t(
                                                                'fixedAssets.detail.scheduleAria',
                                                            )}
                                                            className="text-sm"
                                                        >
                                                            <Table.Header>
                                                                <Table.Column
                                                                    id="period"
                                                                    isRowHeader
                                                                >
                                                                    {t(
                                                                        'fixedAssets.detail.period',
                                                                    )}
                                                                </Table.Column>
                                                                <Table.Column id="charge">
                                                                    {t(
                                                                        'fixedAssets.run.charge',
                                                                    )}
                                                                </Table.Column>
                                                                <Table.Column id="accumulated">
                                                                    {t(
                                                                        'fixedAssets.columns.accumulated',
                                                                    )}
                                                                </Table.Column>
                                                                <Table.Column id="netBookValue">
                                                                    {t(
                                                                        'fixedAssets.columns.netBookValue',
                                                                    )}
                                                                </Table.Column>
                                                            </Table.Header>
                                                            <Table.Body>
                                                                {scheduleRows.map(
                                                                    (row) => (
                                                                        <Table.Row
                                                                            className={cn(
                                                                                row.posted
                                                                                    ? 'text-foreground'
                                                                                    : 'text-muted',
                                                                            )}
                                                                            id={
                                                                                row.period
                                                                            }
                                                                            key={
                                                                                row.period
                                                                            }
                                                                            {...getNodeTestIdProp(
                                                                                `fixed-assets-schedule-row-${row.period}`,
                                                                            )}
                                                                        >
                                                                            <Table.Cell>
                                                                                <span className="flex items-center gap-2">
                                                                                    {
                                                                                        row.periodText
                                                                                    }
                                                                                    {row.posted ? (
                                                                                        <span className="rounded bg-success-soft px-1.5 py-0.5 text-[10px] font-medium text-success-soft-foreground">
                                                                                            {t(
                                                                                                'fixedAssets.detail.posted',
                                                                                            )}
                                                                                        </span>
                                                                                    ) : null}
                                                                                </span>
                                                                            </Table.Cell>
                                                                            <Table.Cell>
                                                                                <span className="block whitespace-nowrap text-right tabular-nums">
                                                                                    {
                                                                                        row.amountText
                                                                                    }
                                                                                </span>
                                                                            </Table.Cell>
                                                                            <Table.Cell>
                                                                                <span className="block whitespace-nowrap text-right tabular-nums">
                                                                                    {
                                                                                        row.accumulatedText
                                                                                    }
                                                                                </span>
                                                                            </Table.Cell>
                                                                            <Table.Cell>
                                                                                <span className="block whitespace-nowrap text-right tabular-nums">
                                                                                    {
                                                                                        row.netBookValueText
                                                                                    }
                                                                                </span>
                                                                            </Table.Cell>
                                                                        </Table.Row>
                                                                    ),
                                                                )}
                                                            </Table.Body>
                                                        </Table.Content>
                                                    </Table.ScrollContainer>
                                                </Table>
                                            </div>
                                        )}
                                    </div>

                                    {/* Audit trail */}
                                    <div className="flex flex-col gap-2">
                                        <span className="text-xs font-medium uppercase tracking-wide text-muted">
                                            {t('fixedAssets.detail.entries')}
                                        </span>
                                        {detail.entries.length === 0 ? (
                                            <p
                                                className="text-sm text-muted"
                                                {...getNodeTestIdProp(
                                                    'fixed-assets-detail-no-entries',
                                                )}
                                            >
                                                {t(
                                                    'fixedAssets.detail.noEntries',
                                                )}
                                            </p>
                                        ) : (
                                            <ul className="flex flex-col gap-1">
                                                {detail.entries.map((entry) => (
                                                    <li
                                                        className="flex items-center justify-between gap-3 rounded-lg bg-surface-secondary px-3 py-2"
                                                        key={`${entry.kind}-${entry.journalEntryId}-${entry.period ?? ''}`}
                                                    >
                                                        <span className="flex min-w-0 flex-col">
                                                            <span className="text-sm text-foreground">
                                                                {t(
                                                                    ENTRY_KIND_KEY[
                                                                        entry
                                                                            .kind
                                                                    ],
                                                                )}
                                                                {entry.period
                                                                    ? ` · ${formatPeriod(entry.period, t)}`
                                                                    : ''}
                                                            </span>
                                                            <span className="text-xs tabular-nums text-muted">
                                                                {isoToDisplayDate(
                                                                    entry.date,
                                                                )}{' '}
                                                                ·{' '}
                                                                {formatGel(
                                                                    entry.amountTetri,
                                                                )}
                                                            </span>
                                                        </span>
                                                        <Button
                                                            onPress={() =>
                                                                onOpenEntry(
                                                                    entry.journalEntryId,
                                                                    entry.date,
                                                                )
                                                            }
                                                            size="sm"
                                                            variant="outline"
                                                            {...getNodeTestIdProp(
                                                                `fixed-assets-detail-entry-${entry.journalEntryId}`,
                                                            )}
                                                        >
                                                            {t(
                                                                'fixedAssets.detail.openEntry',
                                                            )}
                                                        </Button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <p className="py-10 text-center text-sm text-muted">
                                    {t('fixedAssets.detail.notFound')}
                                </p>
                            )}
                        </Sheet.Body>

                        <Sheet.Footer>
                            <Button
                                onPress={onClose}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'fixed-assets-detail-close',
                                )}
                            >
                                {t('fixedAssets.actions.close')}
                            </Button>
                            {asset && asset.status === 'ACTIVE' ? (
                                <>
                                    <Button
                                        onPress={() => onDispose(asset.id)}
                                        variant="outline"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-detail-dispose',
                                        )}
                                    >
                                        <Icon
                                            aria-hidden
                                            icon="gravity-ui:box-arrow-up"
                                        />
                                        {t('fixedAssets.actions.dispose')}
                                    </Button>
                                    <Button
                                        onPress={() => onEdit(asset.id)}
                                        variant="primary"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-detail-edit',
                                        )}
                                    >
                                        <Icon
                                            aria-hidden
                                            icon="gravity-ui:pencil"
                                        />
                                        {t('fixedAssets.actions.edit')}
                                    </Button>
                                </>
                            ) : null}
                        </Sheet.Footer>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
        </Sheet>
    );
};
